import axios from 'axios'
import { useAuthStore } from '../stores/authStore'
import { mockApi } from './mock'

// 是否启用mock模式
const ENABLE_MOCK = import.meta.env.VITE_ENABLE_MOCK_API === 'true'

// 创建axios实例
const apiClient = axios.create({
  baseURL: '/api', // 通过Vite代理
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器 - 添加token
apiClient.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState()
    
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器 - 处理token刷新和错误
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    
    // 如果是401错误且未重试过，尝试刷新token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      let currentRefreshToken: string | null = null
      
      try {
        const { refreshToken } = useAuthStore.getState()
        currentRefreshToken = refreshToken

        if (!refreshToken) {
          // 无刷新令牌时，说明用户从未登录过或已登出
          // 不清除 auth（已经是干净的），也不重定向（public pages 容忍 401）
          return Promise.reject(new Error('未登录'))
        }

        // 调用刷新token接口
        const response = await axios.post('/api/v1/auth/refresh', {
          refresh_token: refreshToken
        })
        
        const { access_token, refresh_token } = response.data
        
        // 更新store中的tokens
        useAuthStore.getState().setTokens(access_token, refresh_token)
        
        // 更新原请求的Authorization头
        originalRequest.headers.Authorization = `Bearer ${access_token}`
        
        // 重新发送原请求
        return apiClient(originalRequest)
      } catch (refreshError) {
        // 刷新失败，清除认证状态
        useAuthStore.getState().clearAuth()
        // 未登录用户的 401 不重定向（PricingPage 等公开页面调用受保护 API 时）
        // 仅当之前已有 refreshToken 时才重定向到 /login
        if (currentRefreshToken) {
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
          window.location.href = `/login?redirect=${returnUrl}`
        }
        return Promise.reject(refreshError)
      }
    }
    
    // 其他错误处理
    if (error.response?.status === 403) {
      // 权限不足
      console.error('权限不足')
    }
    
    return Promise.reject(error)
  }
)

// 增强的API调用函数，支持mock模式
export const apiCall = async <T>(
  method: 'get' | 'post' | 'put' | 'delete' | 'patch',
  url: string,
  data?: any,
  config?: any
): Promise<T> => {
  // 如果启用mock模式且是开发环境，使用mock数据
  if (ENABLE_MOCK && import.meta.env.DEV) {
    console.log(`[Mock API] ${method.toUpperCase()} ${url}`)
    
    // 解析URL路径，映射到对应的mock函数
    const path = url.replace('/api', '').replace('/v1', '')
    
    // 简单的URL到mock函数的映射
    if (path.startsWith('/auth/login') && method === 'post') {
      return mockApi.auth.login(data.username, data.password) as Promise<T>
    }
    
    if (path.startsWith('/auth/me') && method === 'get') {
      return mockApi.auth.getCurrentUser() as Promise<T>
    }
    
    if (path.startsWith('/dashboard/stats') && method === 'get') {
      return mockApi.dashboard.getDashboardStats() as Promise<T>
    }
    
    if (path.startsWith('/novels') && method === 'get') {
      // Handle /novels/simple and /novels (with pagination)
      if (path.endsWith('/simple')) {
        return mockApi.novel.getSimpleList() as Promise<T>
      }
      // Single novel get: /novels/{id}
      if (path.match(/^\/novels\/(?!simple)[\w-]+$/) && !path.includes('?')) {
        const id = path.split('/').pop()!
        return mockApi.novel.getNovel(id) as Promise<T>
      }
      // List novels: /novels
      const params = config?.params || {}
      return mockApi.novel.getNovels(params.page, params.pageSize) as Promise<T>
    }

    if (path.match(/^\/novels\/(?!simple)[\w-]+$/) && method === 'put') {
      const id = path.split('/').pop()!
      return mockApi.novel.updateNovel(id, data) as Promise<T>
    }

    if (path.match(/^\/novels\/(?!simple)[\w-]+$/) && method === 'delete') {
      const id = path.split('/').pop()!
      return mockApi.novel.deleteNovel(id) as Promise<T>
    }

    if (path.startsWith('/novels') && method === 'post') {
      return mockApi.novel.createNovel(data) as Promise<T>
    }

    // Chapter API routing
    if (path.startsWith('/chapters') && method === 'get') {
      if (path.match(/^\/chapters\/[\w-]+$/)) {
        const id = path.split('/').pop()!
        return mockApi.chapter.getChapter(id) as Promise<T>
      }
      const params = config?.params || {}
      return mockApi.chapter.getChapters(params) as Promise<T>
    }

    if (path.startsWith('/chapters') && method === 'post') {
      if (path.endsWith('/batch/status')) {
        const { ids, status } = data
        return mockApi.chapter.batchUpdateStatus(ids, status) as Promise<T>
      }
      if (path.endsWith('/batch/delete')) {
        const { ids } = data
        return mockApi.chapter.batchDelete(ids) as Promise<T>
      }
      return mockApi.chapter.createChapter(data) as Promise<T>
    }

    if (path.match(/^\/chapters\/[\w-]+$/) && method === 'put') {
      const id = path.split('/').pop()!
      return mockApi.chapter.updateChapter(id, data) as Promise<T>
    }

    if (path.match(/^\/chapters\/[\w-]+\/status$/) && method === 'patch') {
      const id = path.split('/')[2]
      return mockApi.chapter.updateChapterStatus(id, data.status) as Promise<T>
    }

    if (path.match(/^\/chapters\/[\w-]+$/) && method === 'delete') {
      const id = path.split('/').pop()!
      return mockApi.chapter.deleteChapter(id) as Promise<T>
    }

    if (path.startsWith('/system/monitoring') && method === 'get') {
      return mockApi.system.getMonitoringData() as Promise<T>
    }

    if (path.startsWith('/users') && method === 'get') {
      return mockApi.user.getUsers(data) as Promise<T>
    }

    if (path.match(/^\/users\/[\w-]+$/) && method === 'put') {
      const id = path.split('/').pop()!
      return mockApi.user.updateUser(id, data) as Promise<T>
    }

    if (path.match(/^\/users\/[\w-]+$/) && method === 'delete') {
      const id = path.split('/').pop()!
      return mockApi.user.deleteUser(id) as Promise<T>
    }

    // 默认返回成功响应
    return Promise.resolve({
      success: true,
      data: {},
      message: 'Mock response',
      timestamp: new Date().toISOString(),
    } as any)
  }
  
  // 真实API调用
  try {
    // Strip /api prefix if present (baseURL already includes /api)
    const cleanUrl = url.startsWith('/api') ? url.substring(4) : url
    const response = await apiClient.request({
      method,
      url: cleanUrl,
      data,
      ...config,
    })
    return response.data
  } catch (error) {
    console.error(`API call failed: ${method} ${url}`, error)
    throw error
  }
}

// 导出原始的axios实例（用于特殊情况）
export { apiClient }

export default apiCall